import React, { useMemo, useState } from 'react';
import SectionCard from '../controls/SectionCard';
import Button from '../controls/Button';
import Toggle from '../controls/Toggle';
import styles from './ArrangeSection.module.css';
import {
  alignElements,
  bringForward,
  bringToFront,
  distributeElements,
  sendBackward,
  sendToBack,
} from '../../../utils/zIndexUtils';

const ArrangeSection = ({
  model,
  elements,
  selectedIds,
  setElementsLocal,
  commitElementsToFirestore,
  artboardWidth,
  artboardHeight,
}) => {
  const [alignToPage, setAlignToPage] = useState(false);
  const unlockedSelectedIds = useMemo(
    () => selectedIds.filter((id) => {
      const element = elements.find((el) => el.id === id);
      return element && !element.locked;
    }),
    [elements, selectedIds],
  );
  const hasUnlockedSelection = unlockedSelectedIds.length > 0;
  if (!selectedIds.length) {
    return null;
  }

  const apply = (nextElements, reason) => {
    if (!hasUnlockedSelection) return;
    setElementsLocal(nextElements);
    commitElementsToFirestore(nextElements, reason);
  };

  const handleAlign = (align) => {
    const next = alignElements(elements, unlockedSelectedIds, align, {
      artboardWidth,
      artboardHeight,
      alignToPage,
    });
    apply(next, `align-${align}`);
  };

  const handleDistribute = (axis) => {
    const next = distributeElements(elements, unlockedSelectedIds, axis);
    apply(next, `distribute-${axis}`);
  };

  return (
    <SectionCard title="Arrange">
      <div className={styles.row}>
        <Button
          onClick={() => apply(bringForward(elements, unlockedSelectedIds), 'bring-forward')}
          disabled={!hasUnlockedSelection}
        >
          Bring forward
        </Button>
        <Button
          onClick={() => apply(bringToFront(elements, unlockedSelectedIds), 'bring-front')}
          disabled={!hasUnlockedSelection}
        >
          Bring to front
        </Button>
        <Button
          onClick={() => apply(sendBackward(elements, unlockedSelectedIds), 'send-backward')}
          disabled={!hasUnlockedSelection}
        >
          Send backward
        </Button>
        <Button
          onClick={() => apply(sendToBack(elements, unlockedSelectedIds), 'send-back')}
          disabled={!hasUnlockedSelection}
        >
          Send to back
        </Button>
      </div>
      <div className={styles.row}>
        <Toggle
          checked={alignToPage}
          onChange={() => setAlignToPage((prev) => !prev)}
          label="Align to page"
          disabled={!hasUnlockedSelection}
        />
      </div>
      <div className={styles.alignGrid}>
        <Button onClick={() => handleAlign('left')} disabled={!hasUnlockedSelection}>
          Align left
        </Button>
        <Button onClick={() => handleAlign('center')} disabled={!hasUnlockedSelection}>
          Align center
        </Button>
        <Button onClick={() => handleAlign('right')} disabled={!hasUnlockedSelection}>
          Align right
        </Button>
        <Button onClick={() => handleAlign('top')} disabled={!hasUnlockedSelection}>
          Align top
        </Button>
        <Button onClick={() => handleAlign('middle')} disabled={!hasUnlockedSelection}>
          Align middle
        </Button>
        <Button onClick={() => handleAlign('bottom')} disabled={!hasUnlockedSelection}>
          Align bottom
        </Button>
      </div>
      <div className={styles.alignGrid}>
        <Button
          onClick={() => handleDistribute('horizontal')}
          disabled={unlockedSelectedIds.length < 3}
        >
          Distribute H
        </Button>
        <Button
          onClick={() => handleDistribute('vertical')}
          disabled={unlockedSelectedIds.length < 3}
        >
          Distribute V
        </Button>
      </div>
    </SectionCard>
  );
};

export default ArrangeSection;
